import React, { forwardRef, useState } from 'react';
import { EyeCloseIcon, EyeIcon, Xclose } from '../assets/svg/SignInIcons';

interface InputProps {
  label: string;
  type?: string;
  error?: string | React.ReactNode;
  ariaLabel: string;
  autoComplete?: string;
  placeHolder?: string;
  onClear?: () => void;
  showClearButton?: boolean;
  defaultValue?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onInput?: (e: React.FormEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}
//   onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
// }

export const InputBox = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      type,
      error,
      ariaLabel,
      autoComplete,
      placeHolder,
      onClear,
      showClearButton,
      defaultValue,
      onChange,
      onInput,
      ...rest
    },
    ref
  ) => {
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      const input = e.target as HTMLInputElement;
      if (
        (e.key === 'Backspace' || e.key === 'Delete') &&
        input.selectionStart === 0 &&
        input.selectionEnd === input.value.length
      ) {
        onClear && onClear();
      }
      if (e.key === ' ' || e.key === 'Spacebar') {
        onClear && onClear();
      }
    };
    return (
      <div className="relative">
        <label
          className="block text-sm font-medium text-gray-700"
          htmlFor={label}
        >
          {label}
        </label>
        <div className="relative">
          <input
            type={type}
            id={label}
            ref={ref}
            aria-label={ariaLabel}
            aria-invalid={!!error}
            placeholder={placeHolder}
            autoComplete={autoComplete}
            defaultValue={defaultValue}
            {...rest}
            onChange={(e) => {
              onChange && onChange(e);
            }}
            onInput={onInput}
            onKeyDown={(e) => {
              handleKeyDown(e);
            }}
            className={`mt-1 block w-full p-2 border min-h-[48px] ${
              error
                ? 'border-primary-red focus:ring-primary-red focus:outline-none focus:rounded-none'
                : 'border-gray-300  focus:rounded-none'
            } `}
          />
          {showClearButton && (
            <button
              type="button"
              aria-label="Close"
              onClick={onClear}
              className="absolute right-2 top-2 text-gray-400 hover:text-gray-600 focus:outline-none"
            >
              <Xclose />
            </button>
          )}
        </div>
        {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
      </div>
    );
  }
);

export const PasswordInput = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, ariaLabel, showClearButton, ...rest }, ref) => {
    const [showPassword, setShowPassword] = useState(false);

    return (
      <div className="relative">
        <InputBox
          type={showPassword ? 'text' : 'password'}
          label={label}
          ref={ref}
          error={error}
          ariaLabel={ariaLabel}
          {...rest}
        />
        <button
          type="button"
          onClick={() => setShowPassword((prev) => !prev)}
          className="absolute right-2 top-[2.2rem] text-gray-400 hover:text-gray-600 focus:outline-none"
          aria-label="Show password"
        >
          {showClearButton && (
            <div>{showPassword ? <EyeIcon /> : <EyeCloseIcon />}</div>
          )}
        </button>
      </div>
    );
  }
);

interface CheckboxProps {
  label: string;
  ariaLabel: string;
  onChange?: () => void;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, ariaLabel, onChange, ...rest }, ref) => (
    <div className="flex items-center">
      <input
        type="checkbox"
        id={label}
        ref={ref}
        aria-label={ariaLabel}
        onChange={onChange}
        className="h-5 w-5 appearance-none bg-white border border-gray-300 rounded-sm checked:bg-red-600 focus:ring-red-500 cursor-pointer
               relative checked:after:absolute checked:after:content-['✓'] checked:after:text-white  checked:after:text-sm checked:after:left-[4px] checked:after:top-[-2px] font-bold "
        {...rest}
      />
      <label htmlFor={label} className="ml-2 block text-sm text-gray-900">
        {label}
      </label>
    </div>
  )
);

interface ButtonProps {
  text: string;
  disabled?: boolean;
  loader?: boolean;
  onClick?: () => void;
  'aria-busy'?: boolean;
  'aria-label'?: string;
}

export const Button: React.FC<ButtonProps> = ({
  text,
  disabled = false,
  onClick,
  'aria-busy': ariaBusy,
  'aria-label': ariaLabel,
}) => (
  <button
    aria-label={ariaLabel || text}
    aria-busy={ariaBusy}
    type="submit"
    disabled={disabled}
    onClick={onClick}
    className={`${disabled ? 'bg-disabled-bg-color text-Text-disabled-btn border-[#D9D9D9] border min-h-[48px]' : 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500'} min-h-[48px] w-full  font-medium py-2 px-4 focus:outline-none focus:ring-2 focus:ring-opacity-50 h-10"`}
  >
    {text}
  </button>
);
